import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { SystemPromptsManager, BackgroundThemesManager } from './AIProductsManagerExtensions';
import { ContentSafetyManager, JobQueueMonitor } from './AIProductsManagerExtensions2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// AI Products Manager Component
export const AIProductsManager = () => {
  const theme = useThemeClasses();
  const [activeTab, setActiveTab] = useState('config'); // config, categories, test
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">AI Products Manager</h2>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('config')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'config'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🤖 AI Configuration
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'categories'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📦 Product Categories
          </button>
          <button
            onClick={() => setActiveTab('lithophane-config')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'lithophane-config'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🖼️ Lithophane Configuration
          </button>
          <button
            onClick={() => setActiveTab('test')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'test'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🧪 Test Console
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'analytics'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📊 Analytics & Logs
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pricing'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            💰 Pricing & Costs
          </button>
          <button
            onClick={() => setActiveTab('prompts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'prompts'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📝 System Prompts
          </button>
          <button
            onClick={() => setActiveTab('themes')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'themes'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🎨 Background Themes
          </button>
          <button
            onClick={() => setActiveTab('safety')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'safety'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🛡️ Content Safety
          </button>
          <button
            onClick={() => setActiveTab('jobs')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'jobs'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            ⚙️ Job Queue
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'config' && <AIConfiguration />}
      {activeTab === 'categories' && <CategoryManagement />}
      {activeTab === 'lithophane-config' && <LithophaneConfiguration />}
      {activeTab === 'test' && <TestConsole />}
      {activeTab === 'analytics' && <AnalyticsLogs />}
      {activeTab === 'pricing' && <PricingManagement />}
      {activeTab === 'prompts' && <SystemPromptsManager />}
      {activeTab === 'themes' && <BackgroundThemesManager />}
      {activeTab === 'safety' && <ContentSafetyManager />}
      {activeTab === 'jobs' && <JobQueueMonitor />}
    </div>
  );
};

// AI Configuration Component
const AIConfiguration = () => {
  const theme = useThemeClasses();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    provider: 'claude',
    claude_config: {
      api_key: '',
      model: 'claude-4-sonnet-20250514',
      max_tokens: 4096,
      temperature: 0.7,
      top_p: 0.9
    },
    chatgpt_config: {
      api_key: '',
      model: 'gpt-5',
      max_tokens: 4096,
      temperature: 0.7,
      top_p: 0.9
    },
    fallback_config: {
      primary_provider: 'claude',
      fallback_provider: 'chatgpt',
      auto_switch: true,
      retry_attempts: 3,
      timeout: 30
    },
    use_emergent_key: true,
    system_prompt: '',
    image_generation_config: {
      provider: 'openai',
      model: 'dall-e-3',
      quality: 'standard',
      style: 'vivid',
      size: '1024x1024'
    }
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/ai-settings`);
      setSettings(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error('Failed to load AI settings:', error);
      toast.error('Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API}/admin/ai-settings`, formData);
      toast.success('AI settings updated successfully');
      loadSettings();
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (provider) => {
    try {
      const testData = {
        provider: provider,
        api_key: provider === 'claude' ? formData.claude_config.api_key : formData.chatgpt_config.api_key,
        model: provider === 'claude' ? formData.claude_config.model : formData.chatgpt_config.model,
        test_prompt: 'Say "Connection successful!" and nothing else.'
      };

      const response = await axios.post(`${API}/admin/ai-products/test`, testData);
      
      if (response.data.success) {
        toast.success(`${provider} connection successful! Response time: ${response.data.response_time_seconds}s`);
      } else {
        toast.error(`${provider} connection failed: ${response.data.error}`);
      }
    } catch (error) {
      toast.error(`Failed to test ${provider}: ${error.message}`);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center p-8">Loading AI settings...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* AI Service Provider */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">AI Service Provider</h3>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              value="claude"
              checked={formData.provider === 'claude'}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              className="text-purple-600"
            />
            <span>Claude (Anthropic)</span>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              value="chatgpt"
              checked={formData.provider === 'chatgpt'}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              className="text-purple-600"
            />
            <span>ChatGPT (OpenAI)</span>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              value="both"
              checked={formData.provider === 'both'}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              className="text-purple-600"
            />
            <span>Both (Load Balance / Fallback)</span>
          </label>
        </div>
      </div>

      {/* Emergent LLM Key Option */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            checked={formData.use_emergent_key}
            onChange={(e) => setFormData({ ...formData, use_emergent_key: e.target.checked })}
            className="mt-1 text-purple-600"
          />
          <div>
            <h4 className="font-semibold">Use Emergent LLM Key (Universal Key)</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Use the universal Emergent LLM key as a fallback. This key works with Claude and ChatGPT. 
              Credits are deducted from your key balance.
            </p>
          </div>
        </div>
      </div>

      {/* Claude Configuration */}
      {(formData.provider === 'claude' || formData.provider === 'both') && (
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Claude Configuration</h3>
            <button
              type="button"
              onClick={() => testConnection('claude')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              Test Connection
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">API Key (Optional - uses Emergent key as fallback)</label>
              <input
                type="password"
                value={formData.claude_config.api_key || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  claude_config: { ...formData.claude_config, api_key: e.target.value }
                })}
                placeholder="sk-ant-..."
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Model</label>
              <select
                value={formData.claude_config.model}
                onChange={(e) => setFormData({
                  ...formData,
                  claude_config: { ...formData.claude_config, model: e.target.value }
                })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              >
                <option value="claude-4-sonnet-20250514">Claude 4 Sonnet (Latest)</option>
                <option value="claude-4-opus-20250514">Claude 4 Opus</option>
                <option value="claude-3-7-sonnet-20250219">Claude 3.7 Sonnet</option>
                <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Max Tokens</label>
                <input
                  type="number"
                  value={formData.claude_config.max_tokens}
                  onChange={(e) => setFormData({
                    ...formData,
                    claude_config: { ...formData.claude_config, max_tokens: parseInt(e.target.value) }
                  })}
                  className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Temperature (0-1)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={formData.claude_config.temperature}
                  onChange={(e) => setFormData({
                    ...formData,
                    claude_config: { ...formData.claude_config, temperature: parseFloat(e.target.value) }
                  })}
                  className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ChatGPT Configuration */}
      {(formData.provider === 'chatgpt' || formData.provider === 'both') && (
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">ChatGPT Configuration</h3>
            <button
              type="button"
              onClick={() => testConnection('chatgpt')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              Test Connection
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">API Key (Optional - uses Emergent key as fallback)</label>
              <input
                type="password"
                value={formData.chatgpt_config.api_key || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  chatgpt_config: { ...formData.chatgpt_config, api_key: e.target.value }
                })}
                placeholder="sk-..."
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Model</label>
              <select
                value={formData.chatgpt_config.model}
                onChange={(e) => setFormData({
                  ...formData,
                  chatgpt_config: { ...formData.chatgpt_config, model: e.target.value }
                })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              >
                <option value="gpt-5">GPT-5 (Latest)</option>
                <option value="gpt-5-mini">GPT-5 Mini</option>
                <option value="gpt-4.5-preview">GPT-4.5 Preview</option>
                <option value="gpt-4o">GPT-4o</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Max Tokens</label>
                <input
                  type="number"
                  value={formData.chatgpt_config.max_tokens}
                  onChange={(e) => setFormData({
                    ...formData,
                    chatgpt_config: { ...formData.chatgpt_config, max_tokens: parseInt(e.target.value) }
                  })}
                  className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Temperature (0-1)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={formData.chatgpt_config.temperature}
                  onChange={(e) => setFormData({
                    ...formData,
                    chatgpt_config: { ...formData.chatgpt_config, temperature: parseFloat(e.target.value) }
                  })}
                  className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fallback & Redundancy */}
      {formData.provider === 'both' && (
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <h3 className="text-lg font-semibold mb-4">Fallback & Redundancy</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Primary Provider</label>
                <select
                  value={formData.fallback_config.primary_provider}
                  onChange={(e) => setFormData({
                    ...formData,
                    fallback_config: { ...formData.fallback_config, primary_provider: e.target.value }
                  })}
                  className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
                >
                  <option value="claude">Claude</option>
                  <option value="chatgpt">ChatGPT</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Fallback Provider</label>
                <select
                  value={formData.fallback_config.fallback_provider}
                  onChange={(e) => setFormData({
                    ...formData,
                    fallback_config: { ...formData.fallback_config, fallback_provider: e.target.value }
                  })}
                  className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
                >
                  <option value="claude">Claude</option>
                  <option value="chatgpt">ChatGPT</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.fallback_config.auto_switch}
                onChange={(e) => setFormData({
                  ...formData,
                  fallback_config: { ...formData.fallback_config, auto_switch: e.target.checked }
                })}
                className="text-purple-600"
              />
              <label className="text-sm">Auto-switch on failure</label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Retry Attempts</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.fallback_config.retry_attempts}
                  onChange={(e) => setFormData({
                    ...formData,
                    fallback_config: { ...formData.fallback_config, retry_attempts: parseInt(e.target.value) }
                  })}
                  className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Timeout (seconds)</label>
                <input
                  type="number"
                  min="10"
                  max="120"
                  value={formData.fallback_config.timeout}
                  onChange={(e) => setFormData({
                    ...formData,
                    fallback_config: { ...formData.fallback_config, timeout: parseInt(e.target.value) }
                  })}
                  className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Generation Settings */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">🎨 Image Generation Settings</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Image Model</label>
            <select
              value={formData.image_generation_config?.model || 'dall-e-3'}
              onChange={(e) => setFormData({
                ...formData,
                image_generation_config: {
                  ...formData.image_generation_config,
                  model: e.target.value
                }
              })}
              className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
            >
              <option value="dall-e-3">DALL-E 3 (Latest - Recommended)</option>
              <option value="dall-e-2">DALL-E 2 (Legacy)</option>
              <option value="gpt-image-1">GPT Image-1 (Requires Org Verification)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Model used for AI background generation</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Image Quality</label>
            <select
              value={formData.image_generation_config?.quality || 'standard'}
              onChange={(e) => setFormData({
                ...formData,
                image_generation_config: {
                  ...formData.image_generation_config,
                  quality: e.target.value
                }
              })}
              className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
            >
              <option value="standard">Standard (Faster, Lower Cost)</option>
              <option value="hd">HD (Slower, Higher Cost)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Image Style (DALL-E only)</label>
            <select
              value={formData.image_generation_config?.style || 'vivid'}
              onChange={(e) => setFormData({
                ...formData,
                image_generation_config: {
                  ...formData.image_generation_config,
                  style: e.target.value
                }
              })}
              className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
            >
              <option value="vivid">Vivid (More Creative)</option>
              <option value="natural">Natural (More Realistic)</option>
            </select>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h5 className="font-semibold text-sm mb-2">💡 Background Removal</h5>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Background removal uses AI-enhanced algorithms with edge detection and color clustering. 
              Configuration is automatic based on the selected image model.
            </p>
          </div>
        </div>
      </div>

      {/* System Prompt */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">System Prompt</h3>
        <textarea
          value={formData.system_prompt}
          onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
          rows="6"
          placeholder="You are a 3D design assistant..."
          className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
        />
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          This prompt is sent with every AI request to set the behavior and context.
        </p>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save AI Settings'}
        </button>
      </div>
    </form>
  );
};

// Category Management Component  
const CategoryManagement = () => {
  const theme = useThemeClasses();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    base_price: 15.00,
    price_per_complexity: 2.0,
    icon: '✨',
    is_active: true,
    custom_prompt: '',
    max_size_mm: 200
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/ai-products/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/ai-products/categories/${editingCategory.id}`, formData);
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/admin/ai-products/categories`, formData);
        toast.success('Category created successfully');
      }
      
      setShowForm(false);
      setEditingCategory(null);
      resetForm();
      loadCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error('Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      base_price: category.base_price,
      price_per_complexity: category.price_per_complexity || 2.0,
      icon: category.icon,
      is_active: category.is_active,
      custom_prompt: category.custom_prompt || '',
      max_size_mm: category.max_size_mm || 200
    });
    setShowForm(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      await axios.delete(`${API}/admin/ai-products/categories/${categoryId}`);
      toast.success('Category deleted successfully');
      loadCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Failed to delete category');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      base_price: 15.00,
      price_per_complexity: 2.0,
      icon: '✨',
      is_active: true,
      custom_prompt: '',
      max_size_mm: 200
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center p-8">Loading categories...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">AI Product Categories</h3>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingCategory(null);
            resetForm();
          }}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          {showForm ? 'Cancel' : '+ Add New Category'}
        </button>
      </div>

      {/* Category Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className={`${theme.card} rounded-lg shadow p-6 space-y-4`}>
          <h4 className="font-semibold text-lg">
            {editingCategory ? 'Edit Category' : 'New Category'}
          </h4>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Category Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Icon</label>
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="✨"
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows="3"
              className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Base Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.base_price}
                onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) })}
                required
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Price per Complexity ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price_per_complexity}
                onChange={(e) => setFormData({ ...formData, price_per_complexity: parseFloat(e.target.value) })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Max Size (mm)</label>
              <input
                type="number"
                value={formData.max_size_mm}
                onChange={(e) => setFormData({ ...formData, max_size_mm: parseInt(e.target.value) })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Custom AI Prompt (Optional)</label>
            <textarea
              value={formData.custom_prompt}
              onChange={(e) => setFormData({ ...formData, custom_prompt: e.target.value })}
              rows="4"
              placeholder="Additional instructions for the AI when generating this category..."
              className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
            />
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="text-purple-600"
            />
            <label className="text-sm">Active (visible to customers)</label>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingCategory(null);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              {editingCategory ? 'Update Category' : 'Create Category'}
            </button>
          </div>
        </form>
      )}

      {/* Categories List */}
      <div className="grid gap-4">
        {categories.map((category) => (
          <div key={category.id} className={`${theme.card} rounded-lg shadow p-6`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="text-3xl">{category.icon}</span>
                  <div>
                    <h4 className="text-lg font-semibold">{category.name}</h4>
                    <span className={`text-xs px-2 py-1 rounded ${
                      category.is_active 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {category.is_active ? '● Active' : '○ Inactive'}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{category.description}</p>
                <div className="flex space-x-4 text-sm">
                  <span>Base Price: <strong>${category.base_price}</strong></span>
                  <span>Max Size: <strong>{category.max_size_mm}mm</strong></span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(category)}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(category.id)}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Test Console Component
const TestConsole = () => {
  const theme = useThemeClasses();
  const [testProvider, setTestProvider] = useState('claude');
  const [testPrompt, setTestPrompt] = useState('Create a small dragon figurine, 100mm tall, suitable for FDM printing');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const response = await axios.post(`${API}/admin/ai-products/test`, {
        provider: testProvider,
        api_key: null, // Will use configured keys or Emergent key
        model: testProvider === 'claude' ? 'claude-4-sonnet-20250514' : 'gpt-5',
        test_prompt: testPrompt
      });

      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success('Test successful!');
      } else {
        toast.error('Test failed');
      }
    } catch (error) {
      console.error('Test failed:', error);
      toast.error('Test failed');
      setTestResult({
        success: false,
        error: error.message,
        response_time_seconds: 0,
        tokens_used: 0,
        estimated_cost: 0
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">AI Testing Console</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Test Provider</label>
            <select
              value={testProvider}
              onChange={(e) => setTestProvider(e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
            >
              <option value="claude">Claude (uses configured settings)</option>
              <option value="chatgpt">ChatGPT (uses configured settings)</option>
              <option value="emergent">Emergent LLM Key</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Test Prompt</label>
            <textarea
              value={testPrompt}
              onChange={(e) => setTestPrompt(e.target.value)}
              rows="6"
              className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
            />
          </div>

          <button
            onClick={handleTest}
            disabled={testing || !testPrompt}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
          >
            {testing ? '⏳ Generating Test Response...' : '✨ Generate Test Response'}
          </button>
        </div>
      </div>

      {/* Test Results */}
      {testResult && (
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <h4 className="font-semibold text-lg mb-4">
            {testResult.success ? '✅ Test Successful' : '❌ Test Failed'}
          </h4>

          {testResult.success ? (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${theme.bgSecondary}`}>
                <h5 className="font-semibold mb-2">Response Preview:</h5>
                <p className="text-sm whitespace-pre-wrap">{testResult.response_text}</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className={`p-4 rounded-lg ${theme.bgSecondary}`}>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Response Time</p>
                  <p className="text-2xl font-bold">{testResult.response_time_seconds}s</p>
                </div>
                <div className={`p-4 rounded-lg ${theme.bgSecondary}`}>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Tokens Used</p>
                  <p className="text-2xl font-bold">{testResult.tokens_used}</p>
                </div>
                <div className={`p-4 rounded-lg ${theme.bgSecondary}`}>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Estimated Cost</p>
                  <p className="text-2xl font-bold">${testResult.estimated_cost}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-700 dark:text-red-300">{testResult.error}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Analytics & Logs Component
const AnalyticsLogs = () => {
  const theme = useThemeClasses();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadAnalytics();
  }, [days]);

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/ai-products/analytics/dashboard?days=${days}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center p-8">Loading analytics...</div>;
  }

  if (!analytics) {
    return <div className="text-center p-8 text-gray-500">No analytics data available</div>;
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Analytics Dashboard</h3>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className={`px-4 py-2 border rounded-lg ${theme.input}`}
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Generations</p>
          <p className="text-3xl font-bold text-purple-600">{analytics.total_generations}</p>
        </div>
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <p className="text-sm text-gray-600 dark:text-gray-400">Success Rate</p>
          <p className="text-3xl font-bold text-green-600">{analytics.success_rate}%</p>
        </div>
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <p className="text-sm text-gray-600 dark:text-gray-400">Avg Response Time</p>
          <p className="text-3xl font-bold text-blue-600">{analytics.avg_response_time.toFixed(2)}s</p>
        </div>
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Cost</p>
          <p className="text-3xl font-bold text-orange-600">${analytics.total_cost}</p>
        </div>
      </div>

      {/* Provider Breakdown */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h4 className="font-semibold text-lg mb-4">Provider Breakdown</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(analytics.provider_breakdown).map(([provider, count]) => (
            <div key={provider} className={`p-4 rounded-lg ${theme.bgSecondary}`}>
              <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">{provider}</p>
              <p className="text-2xl font-bold">{count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Category Breakdown */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h4 className="font-semibold text-lg mb-4">Category Breakdown</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(analytics.category_breakdown).map(([category, count]) => (
            <div key={category} className={`p-4 rounded-lg ${theme.bgSecondary}`}>
              <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">{category}</p>
              <p className="text-2xl font-bold">{count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Unique Users</p>
          <p className="text-3xl font-bold text-indigo-600">{analytics.unique_users}</p>
        </div>
        <div className={`${theme.card} rounded-lg shadow p-6`}>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Total Tokens Used</p>
          <p className="text-3xl font-bold text-teal-600">{analytics.total_tokens_used.toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

// Pricing Management Component
const PricingManagement = () => {
  const theme = useThemeClasses();
  const [pricingSettings, setPricingSettings] = useState({
    ai_generation_fee: 5.00,
    include_in_product_price: true,
    show_as_separate_line: false,
    first_iterations_free: 3,
    additional_iteration_cost: 2.00,
    budget_alert_threshold: 80,
    monthly_budget_limit: 200.00,
    alert_email: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save pricing settings (would need backend endpoint)
      toast.success('Pricing settings saved successfully');
    } catch (error) {
      toast.error('Failed to save pricing settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Customer Pricing Rules */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">Customer Pricing Rules</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">AI Generation Fee ($)</label>
            <input
              type="number"
              step="0.01"
              value={pricingSettings.ai_generation_fee}
              onChange={(e) => setPricingSettings({ ...pricingSettings, ai_generation_fee: parseFloat(e.target.value) })}
              className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
            />
            <p className="text-xs text-gray-500 mt-1">Additional fee charged per AI design generation</p>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={pricingSettings.include_in_product_price}
              onChange={(e) => setPricingSettings({ ...pricingSettings, include_in_product_price: e.target.checked })}
              className="text-purple-600"
            />
            <label className="text-sm">Include in product price (don't show separately)</label>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={pricingSettings.show_as_separate_line}
              onChange={(e) => setPricingSettings({ ...pricingSettings, show_as_separate_line: e.target.checked })}
              className="text-purple-600"
            />
            <label className="text-sm">Show as separate line item in cart</label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">First Iterations Free</label>
              <input
                type="number"
                value={pricingSettings.first_iterations_free}
                onChange={(e) => setPricingSettings({ ...pricingSettings, first_iterations_free: parseInt(e.target.value) })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Additional Iteration Cost ($)</label>
              <input
                type="number"
                step="0.01"
                value={pricingSettings.additional_iteration_cost}
                onChange={(e) => setPricingSettings({ ...pricingSettings, additional_iteration_cost: parseFloat(e.target.value) })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* AI Cost Tracking & Budgets */}
      <div className={`${theme.card} rounded-lg shadow p-6`}>
        <h3 className="text-lg font-semibold mb-4">AI Cost Tracking & Budget</h3>
        
        <div className="space-y-4">
          <div className={`p-4 rounded-lg ${theme.bgSecondary}`}>
            <h4 className="font-semibold mb-2">Current Month Usage</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Emergent LLM Key:</span>
                <span className="font-bold">$12.45 / $200.00</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div className="bg-purple-600 h-2 rounded-full" style={{ width: '6.2%' }}></div>
              </div>
              <p className="text-xs text-gray-500">6.2% of monthly budget used</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Monthly Budget Limit ($)</label>
              <input
                type="number"
                step="1.00"
                value={pricingSettings.monthly_budget_limit}
                onChange={(e) => setPricingSettings({ ...pricingSettings, monthly_budget_limit: parseFloat(e.target.value) })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Alert at Threshold (%)</label>
              <input
                type="number"
                value={pricingSettings.budget_alert_threshold}
                onChange={(e) => setPricingSettings({ ...pricingSettings, budget_alert_threshold: parseInt(e.target.value) })}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Alert Email</label>
            <input
              type="email"
              value={pricingSettings.alert_email}
              onChange={(e) => setPricingSettings({ ...pricingSettings, alert_email: e.target.value })}
              placeholder="admin@rg3d.ca"
              className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
            />
            <p className="text-xs text-gray-500 mt-1">Receive alerts when budget threshold is reached</p>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              className="text-purple-600"
            />
            <label className="text-sm">Auto-disable AI generation at budget limit</label>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Pricing Settings'}
        </button>
      </div>
    </div>
  );
};

// Lithophane Sphere Configuration Component
const LithophaneConfiguration = () => {
  const theme = useThemeClasses();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSize, setActiveSize] = useState('75mm');
  const [config, setConfig] = useState({
    active_size_profile: '75mm',
    size_profiles: {
      '50mm': {},
      '75mm': {},
      '100mm': {}
    }
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      console.log('[LithophaneConfig] Loading configuration from API...');
      const response = await axios.get(`${API}/ai/lithophane-sphere/config`);
      console.log('[LithophaneConfig] API Response:', response.data);
      if (response.data.success) {
        console.log('[LithophaneConfig] Setting config:', response.data.config);
        console.log('[LithophaneConfig] 75mm profile:', response.data.config.size_profiles['75mm']);
        setConfig(response.data.config);
        setActiveSize(response.data.config.active_size_profile || '75mm');
      }
    } catch (error) {
      console.error('[LithophaneConfig] Failed to load configuration:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/ai/lithophane-sphere/config`, config);
      if (response.data.success) {
        toast.success('Configuration saved successfully!');
      }
    } catch (error) {
      console.error('Failed to save configuration:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleParameterChange = (size, key, value) => {
    setConfig(prev => ({
      ...prev,
      size_profiles: {
        ...prev.size_profiles,
        [size]: {
          ...prev.size_profiles[size],
          [key]: parseFloat(value) || 0
        }
      }
    }));
  };

  const handleSetActiveProfile = (size) => {
    setConfig(prev => ({
      ...prev,
      active_size_profile: size
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-gray-500">Loading configuration...</div>
      </div>
    );
  }

  const currentProfile = config.size_profiles[activeSize] || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${theme.card} rounded-lg shadow-lg p-6`}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-bold">Lithophane Sphere Configuration</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Configure technical parameters for each sphere size profile. These values will be used as defaults for customer orders.
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>

        {/* Active Profile Selector */}
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <label className="block text-sm font-semibold mb-2">Default Active Profile</label>
          <select
            value={config.active_size_profile}
            onChange={(e) => handleSetActiveProfile(e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
          >
            <option value="50mm">50mm</option>
            <option value="75mm">75mm (Recommended)</option>
            <option value="100mm">100mm</option>
          </select>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
            This size will be selected by default for customers
          </p>
        </div>
      </div>

      {/* Size Profile Tabs */}
      <div className={`${theme.card} rounded-lg shadow-lg p-6`}>
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveSize('50mm')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              activeSize === '50mm'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            50mm Profile
          </button>
          <button
            onClick={() => setActiveSize('75mm')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              activeSize === '75mm'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            75mm Profile (Default)
          </button>
          <button
            onClick={() => setActiveSize('100mm')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              activeSize === '100mm'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            100mm Profile
          </button>
        </div>

        {/* Parameter Form */}
        <div className="grid grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            <h4 className="font-semibold text-lg mb-4">Basic Parameters</h4>
            
            <div>
              <label className="block text-sm font-medium mb-1">Lithophane Resolution (mm)</label>
              <input
                type="number"
                step="0.01"
                value={currentProfile.lithophane_resolution_mm || 0}
                onChange={(e) => handleParameterChange(activeSize, 'lithophane_resolution_mm', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Print resolution for lithophane detail</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Sphere Diameter (mm)</label>
              <input
                type="number"
                value={currentProfile.sphere_diameter_mm || 0}
                onChange={(e) => handleParameterChange(activeSize, 'sphere_diameter_mm', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Overall sphere diameter</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Picture Angular Height (degrees)</label>
              <input
                type="number"
                value={currentProfile.picture_angular_height_deg || 0}
                onChange={(e) => handleParameterChange(activeSize, 'picture_angular_height_deg', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Vertical angle coverage for image</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Picture Angular Width (degrees)</label>
              <input
                type="number"
                step="0.01"
                value={currentProfile.picture_angular_width_deg || 0}
                onChange={(e) => handleParameterChange(activeSize, 'picture_angular_width_deg', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Horizontal angle coverage (360° = full wrap)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Maximum Thickness (mm)</label>
              <input
                type="number"
                step="0.1"
                value={currentProfile.max_thickness_mm || 0}
                onChange={(e) => handleParameterChange(activeSize, 'max_thickness_mm', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Max wall thickness (darkest areas)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Minimum Thickness (mm)</label>
              <input
                type="number"
                step="0.1"
                value={currentProfile.min_thickness_mm || 0}
                onChange={(e) => handleParameterChange(activeSize, 'min_thickness_mm', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Min wall thickness (lightest areas)</p>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <h4 className="font-semibold text-lg mb-4">Cylinder & Mounting</h4>
            
            <div>
              <label className="block text-sm font-medium mb-1">Cylinder Outer Diameter (mm)</label>
              <input
                type="number"
                step="0.1"
                value={currentProfile.cylinder_outer_diameter_mm || 0}
                onChange={(e) => handleParameterChange(activeSize, 'cylinder_outer_diameter_mm', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Bottom cylinder diameter</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Cylinder Height (mm)</label>
              <input
                type="number"
                value={currentProfile.cylinder_height_mm || 0}
                onChange={(e) => handleParameterChange(activeSize, 'cylinder_height_mm', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Height of mounting cylinder</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Cylinder Thickness (mm)</label>
              <input
                type="number"
                value={currentProfile.cylinder_thickness_mm || 0}
                onChange={(e) => handleParameterChange(activeSize, 'cylinder_thickness_mm', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Wall thickness of cylinder</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Cylinder Ledge Outer Diameter (mm)</label>
              <input
                type="number"
                value={currentProfile.cylinder_ledge_outer_diameter_mm || 0}
                onChange={(e) => handleParameterChange(activeSize, 'cylinder_ledge_outer_diameter_mm', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Diameter of ledge for mounting</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Top Hole Outer Diameter (mm)</label>
              <input
                type="number"
                value={currentProfile.top_hole_outer_diameter_mm || 0}
                onChange={(e) => handleParameterChange(activeSize, 'top_hole_outer_diameter_mm', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Top opening diameter (0 = closed)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">X Shift (mm)</label>
              <input
                type="number"
                step="0.1"
                value={currentProfile.x_shift_mm || 0}
                onChange={(e) => handleParameterChange(activeSize, 'x_shift_mm', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Horizontal offset adjustment</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Y Shift (mm)</label>
              <input
                type="number"
                step="0.01"
                value={currentProfile.y_shift_mm || 0}
                onChange={(e) => handleParameterChange(activeSize, 'y_shift_mm', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Vertical offset adjustment</p>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h4 className="font-semibold text-lg mb-4">💰 Pricing for {activeSize} Size</h4>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Base Price (CAD $)</label>
              <input
                type="number"
                step="0.01"
                value={currentProfile.base_price || 0}
                onChange={(e) => handleParameterChange(activeSize, 'base_price', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${theme.input}`}
              />
              <p className="text-xs text-gray-500 mt-1">Base price for this size before add-ons</p>
            </div>
            
            {/* Pricing Preview */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <h5 className="font-semibold text-sm mb-2">Customer Pricing Preview</h5>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Base ({activeSize}):</span>
                  <strong>${(currentProfile.base_price || 0).toFixed(2)}</strong>
                </div>
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>+ Display Stand:</span>
                  <span>$9.00</span>
                </div>
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>+ USB Lamp Base:</span>
                  <span>$17.00</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-green-300 dark:border-green-700 font-semibold">
                  <span>Total (with all add-ons):</span>
                  <span className="text-green-600 dark:text-green-400">
                    ${((currentProfile.base_price || 0) + 9 + 17).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? '💾 Saving...' : '💾 Save All Changes'}
          </button>
        </div>
      </div>

      {/* Add-ons Management */}
      <AddonsManagement config={config} setConfig={setConfig} />
    </div>
  );
};

// Add-ons Management Component
const AddonsManagement = ({ config, setConfig }) => {
  const theme = useThemeClasses();
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data || []);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  const addAddon = (productId) => {
    const currentAddons = config.recommended_addons || [];
    if (!currentAddons.includes(productId)) {
      setConfig(prev => ({
        ...prev,
        recommended_addons: [...currentAddons, productId]
      }));
      toast.success('Add-on added');
    }
  };

  const removeAddon = (productId) => {
    setConfig(prev => ({
      ...prev,
      recommended_addons: (prev.recommended_addons || []).filter(id => id !== productId)
    }));
    toast.success('Add-on removed');
  };

  const getAddonProducts = () => {
    const addonIds = config.recommended_addons || [];
    return products.filter(p => addonIds.includes(p.id));
  };

  const getAvailableProducts = () => {
    const addonIds = config.recommended_addons || [];
    return products.filter(p => 
      !addonIds.includes(p.id) && 
      p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <div className={`${theme.card} rounded-lg shadow-lg p-6`}>
      <h3 className="text-xl font-bold mb-4">🎁 Recommended Add-ons</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Select products from your store to recommend as add-ons for the Lithophane Sphere. These will be displayed to customers during checkout.
      </p>

      {/* Current Add-ons */}
      <div className="mb-6">
        <h4 className="font-semibold mb-3">Current Add-ons</h4>
        {getAddonProducts().length === 0 ? (
          <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
            No add-ons selected. Add products from the list below.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {getAddonProducts().map(product => (
              <div key={product.id} className={`${theme.card} border rounded-lg p-4 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  {product.images && product.images.length > 0 && (
                    <img 
                      src={product.images[0]} 
                      alt={product.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                  )}
                  <div>
                    <div className="font-semibold">{product.name}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      ${product.price?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeAddon(product.id)}
                  className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available Products */}
      <div>
        <h4 className="font-semibold mb-3">Add Products</h4>
        
        {/* Search */}
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`w-full px-4 py-2 border rounded-lg mb-4 ${theme.input}`}
        />

        {loadingProducts ? (
          <div className="text-center py-8 text-gray-500">Loading products...</div>
        ) : (
          <div className="max-h-96 overflow-y-auto border rounded-lg">
            {getAvailableProducts().length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No products found
              </div>
            ) : (
              <div className="divide-y">
                {getAvailableProducts().map(product => (
                  <div key={product.id} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800">
                    <div className="flex items-center gap-3">
                      {product.images && product.images.length > 0 && (
                        <img 
                          src={product.images[0]} 
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                      )}
                      <div>
                        <div className="font-medium">{product.name}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          ${product.price?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => addAddon(product.id)}
                      className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIProductsManager;

